import React, {
  createContext,
  useCallback,
  useState,
  useContext,
  useEffect,
} from 'react';
import api from '../services/api';
import { clearStoragePreservingLanguage } from '../shared/i18n';

export interface AuthUser {
  id: string;
  name: string;
  cpf: string;
  birthday: string;
  phone: string;
  email: string;
  screens: string[];
  permissions?: string[];
  isSuperAdmin?: boolean;
}

interface AuthState {
  access_token: string;
  user: AuthUser;
  clinic: string;
}

interface SignInCredentials {
  clinic: string;
  username: string;
  password: string;
}

interface AuthContextData {
  user: AuthUser;
  clinic: string | null;
  isSuperAdmin: boolean;
  signIn(credentials: SignInCredentials): Promise<void>;
  signOut(): void;
  updateUser(user: AuthUser): void;
  screenFind(values: string[]): boolean;
  permissionFind(values: string[]): boolean;
  getToken(): string;
}

interface IAutoProvider {
  children: React.ReactNode;
}

const TOKEN = '@Sigeclin:token';
const USER = '@Sigeclin:user';
const CLINIC = '@Sigeclin:clinic';
const SUPER_ADMIN_EMAIL = process.env.REACT_APP_SUPER_ADMIN_EMAIL?.trim();
const TENANT_MANAGER_ID = process.env.REACT_APP_TENANT_MANAGER_ID?.trim();
const BOOTSTRAP_ADMIN_EMAIL = 'administrador@adm.com';

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

const AuthProvider: React.FC<IAutoProvider> = ({ children }) => {
  const [data, setData] = useState<AuthState>(() => {
    const access_token = sessionStorage.getItem(TOKEN);
    const user = sessionStorage.getItem(USER);
    const clinic = sessionStorage.getItem(CLINIC) || '0';

    if (access_token && user) {
      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      return { access_token, user: JSON.parse(user), clinic };
    }

    return {} as AuthState;
  });

  useEffect(() => {
    if (!data.access_token || !data.user) return;
    const allPerms = data.user.permissions || [];
    const currentScreens = data.user.screens || [];
    const isMissingSessionPayload =
      !allPerms || allPerms.length === 0 || !currentScreens || currentScreens.length === 0;
    if (isMissingSessionPayload) {
      const forced = sessionStorage.getItem('@Sigeclin:force-relogin-once');
      if (forced !== '1') {
        sessionStorage.setItem('@Sigeclin:force-relogin-once', '1');
        clearStoragePreservingLanguage();
        setData({} as AuthState);
        window.location.assign('/');
        return;
      }
    }
    const isScreen = (value: string): boolean => /-view$/i.test(String(value || '').trim());
    const expected = allPerms.filter(isScreen);
    const needsRefresh =
      expected.length > 0 &&
      (expected.length !== currentScreens.length ||
        expected.some((s) => !currentScreens.includes(s)));
    if (!needsRefresh) return;
    const refreshed: AuthUser = {
      ...data.user,
      screens: expected,
    };
    sessionStorage.setItem(USER, JSON.stringify(refreshed));
    setData({
      access_token: data.access_token,
      user: refreshed,
      clinic: data.clinic,
    });
  }, [data.access_token, data.clinic, data.user]);

  const signIn = useCallback(
    async ({ username, password, clinic }: SignInCredentials) => {
      const response = await api.post(
        '/auth',
        {
          username,
          password,
        },
        {
          headers: {
            'x-tenant-id': clinic,
          },
        },
      );

      const { access_token, user } = response.data;

      sessionStorage.setItem(TOKEN, access_token);
      sessionStorage.setItem(USER, JSON.stringify(user));
      sessionStorage.setItem(CLINIC, clinic);

      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      setData({ access_token, user, clinic });
      window.location.assign('/dashboard');
    },
    [],
  );

  const signOut = useCallback(() => {
    clearStoragePreservingLanguage();
    setData({} as AuthState);
    window.location.assign('/');
  }, []);

  const updateUser = useCallback(
    (user: AuthUser) => {
      sessionStorage.setItem(USER, JSON.stringify(user));

      setData({
        access_token: data.access_token,
        user,
        clinic: data.clinic,
      });
    },
    [setData, data.access_token],
  );

  const hasElevatedAccess = useCallback(() => {
    const normalizedEmail = String(data.user?.email ?? '')
      .trim()
      .toLowerCase();

    if (data.user?.isSuperAdmin) {
      return true;
    }

    if (normalizedEmail === BOOTSTRAP_ADMIN_EMAIL) {
      return true;
    }

    return (
      !!SUPER_ADMIN_EMAIL &&
      !!TENANT_MANAGER_ID &&
      normalizedEmail === SUPER_ADMIN_EMAIL.toLowerCase() &&
      data.clinic === TENANT_MANAGER_ID
    );
  }, [data.clinic, data.user]);

  const screenFind = useCallback(
    (values: string[]) => {
      let find = false;
      const isSuperAdmin = hasElevatedAccess();

      if (!data.user) return find;

      if (isSuperAdmin) {
        return true;
      }

      const universalScreens = new Set<string>(['dashboard-view', 'clients-view']);
      const hasUniversal = values.some((val) =>
        universalScreens.has(String(val).toLowerCase()),
      );
      if (hasUniversal) {
        return true;
      }

      const current = data.user.screens || [];
      current.forEach((item) => {
        values.forEach((val) => {
          if (item === val) {
            find = true;
          }
        });
      });
      return find;
    },
    [data.user, hasElevatedAccess],
  );

  const permissionFind = useCallback(
    (values: string[]) => {
      if (!data.user) {
        return false;
      }

      if (hasElevatedAccess()) {
        return true;
      }

      const universalClients = new Set<string>([
        'clients-create',
        'clients-update',
      ]);
      const onlyUniversalClients =
        values.length && values.every((v) => universalClients.has(v));
      if (onlyUniversalClients) {
        return true;
      }

      return values.some((value) =>
        (data.user.permissions || []).includes(value),
      );
    },
    [data.user, hasElevatedAccess],
  );

  const getToken = useCallback(() => {
    return data.access_token;
  }, [data]);

  const isSuperAdmin = hasElevatedAccess();

  return (
    <AuthContext.Provider
      value={{
        user: data.user,
        clinic: data?.clinic || '',
        isSuperAdmin,
        signIn,
        signOut,
        updateUser,
        screenFind,
        permissionFind,
        getToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

function useAuth(): AuthContextData {
  const context = useContext(AuthContext);

  return context;
}

export { AuthProvider, useAuth };
